import React, { createContext, useContext, useState, useEffect } from 'react';
import { StoreSettings, Category } from '../types';

interface StoreContextType {
  settings: StoreSettings;
  categories: Category[];
  loading: boolean;
  refreshSettings: () => Promise<void>;
  refreshCategories: () => Promise<void>;
}

const DEFAULT_SETTINGS: StoreSettings = {
  brand_name: 'Aura Ateliê',
  brand_tagline: 'Design Autoral, Cerâmica & Objetos Vivos',
  brand_description: 'Maison brasileira de cerâmica artesanal, design têxtil e criações sob encomenda.',
  contact_email: 'atendimento@aura-atelie.com.br',
  contact_phone: '+55 11 98765-4321',
  address_line: 'Rua Harmonia, 412 - Vila Madalena, São Paulo - SP',
  cnpj: '48.192.837/0001-90',
  hero_title: 'A Poética da Matéria e do Tempo',
  hero_subtitle: 'Peças em cerâmica moldadas à mão, linho lavado em pedra e criações digitais para o viver contemporâneo.',
  hero_image_url: 'https://images.unsplash.com/photo-1616046229478-9901c5536a45?auto=format&fit=crop&w=1600&q=85',
  announcement_text: 'Frete Cortesia em compras acima de R$ 450 para todo o Brasil • Produção Limitada',
  announcement_enabled: true,
  shipping_flat_rate_cents: 2800,
  shipping_free_threshold_cents: 45000,
  shipping_estimated_days: 5,
};

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<StoreSettings>(DEFAULT_SETTINGS);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = async () => {
    try {
      const res = await fetch('/api/settings');
      if (res.ok) {
        const data = (await res.json()) as any;
        setSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.warn('Could not load remote settings, using defaults', err);
    }
  };

  const refreshCategories = async () => {
    try {
      const res = await fetch('/api/categories');
      if (res.ok) {
        const data = (await res.json()) as any;
        setCategories(data);
      }
    } catch (err) {
      console.warn('Could not load categories', err);
    }
  };

  useEffect(() => {
    Promise.all([refreshSettings(), refreshCategories()]).finally(() => {
      setLoading(false);
    });
  }, []);

  return (
    <StoreContext.Provider
      value={{
        settings,
        categories,
        loading,
        refreshSettings,
        refreshCategories,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore(): StoreContextType {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}
